import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { createNadDebug } from '../utils/debugLog'
import {
  type AdminBanner,
  type BannerAudienceMode,
  type BannerCreateInput,
  type BannerDismissMode,
  type BannerDisplayMode,
} from '../api/banners'
import { POPUP_SEVERITY_CLASSES, type BannerSeverity } from '../components/banners/bannerSeverity'
import {
  useAdminBanners,
  useCreateBanner,
  useDeleteBanner,
  useUpdateBanner,
} from '../hooks/useAdminBanners'
import { resolveBannerText } from '../components/banners/resolveBannerText'
import {
  SUPPORTED_LOCALE_OPTIONS,
  normalizeLocale,
  type SupportedLocale,
} from '../i18n/supportedLocales'
import StickyBannerStrip from '../components/layout/StickyBannerStrip'
import BannerMarkdown from '../components/banners/BannerMarkdown'
import AdminToolNav from '../components/admin/AdminToolNav'

const log = createNadDebug('dw:dynamic-banner:admin')

interface FormState {
  title: string
  message: string
  /**
   * Non-English overrides, keyed by locale. English is NOT here — it lives in
   * `title`/`message`, which are also the fallback any viewer sees when their
   * locale has no entry. Only non-English inputs are offered by the form, so the
   * API's "conflicting en override" 422 is unreachable from this UI.
   */
  title_i18n: Record<string, string>
  message_i18n: Record<string, string>
  display_mode: BannerDisplayMode
  dismiss_mode: BannerDismissMode
  severity: BannerSeverity
  audience_mode: BannerAudienceMode
  start_at: string // datetime-local (browser local); '' = now
  end_at: string // datetime-local; '' = no expiry
  is_active: boolean
}

// Locales an admin can author overrides for: every supported UI locale except the
// English base. Derived, so adding a 4th locale adds form fields automatically
// rather than requiring a hardcoded list to be found and updated.
const TRANSLATABLE_LOCALE_OPTIONS = SUPPORTED_LOCALE_OPTIONS.filter((o) => o.code !== 'en')

const EMPTY_FORM: FormState = {
  title: '',
  message: '',
  title_i18n: {},
  message_i18n: {},
  display_mode: 'banner',
  dismiss_mode: 'recurrent',
  severity: 'info',
  audience_mode: 'all',
  start_at: '',
  end_at: '',
  is_active: true,
}

// Narrow the raw <select> string value into the typed union (avoids `as`).
const toDisplayMode = (v: string): BannerDisplayMode => (v === 'popup' ? 'popup' : 'banner')
const toDismissMode = (v: string): BannerDismissMode =>
  v === 'one_time' ? 'one_time' : 'recurrent'
const toSeverity = (v: string): BannerSeverity =>
  v === 'warning' ? 'warning' : v === 'critical' ? 'critical' : 'info'
const toAudience = (v: string): BannerAudienceMode =>
  v === 'authenticated' ? 'authenticated' : 'all'

/** datetime-local (browser-local) → UTC ISO for the API. Empty → null. */
function localToIso(local: string): string | null {
  if (!local) return null
  const d = new Date(local)
  return isNaN(d.getTime()) ? null : d.toISOString()
}

/** UTC ISO → datetime-local value (browser-local) for editing. */
function isoToLocal(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  const off = d.getTimezoneOffset()
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16)
}

function bannerToForm(b: AdminBanner): FormState {
  return {
    title: b.title,
    message: b.message,
    title_i18n: { ...(b.title_i18n ?? {}) },
    message_i18n: { ...(b.message_i18n ?? {}) },
    display_mode: b.display_mode,
    dismiss_mode: b.dismiss_mode,
    severity: b.severity,
    audience_mode: b.audience_mode,
    start_at: isoToLocal(b.start_at),
    end_at: isoToLocal(b.end_at),
    is_active: b.is_active,
  }
}

/** Drop blank entries so an untouched input never persists `{ko: ''}`. */
function pruneI18n(map: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {}
  for (const [locale, text] of Object.entries(map)) {
    const trimmed = text.trim()
    if (trimmed) out[locale] = trimmed
  }
  return out
}

function formToPayload(f: FormState): BannerCreateInput {
  return {
    title: f.title.trim(),
    message: f.message.trim(),
    // Always sent, on create AND update. The API never clears maps implicitly, so
    // the form round-tripping them is what keeps an edit from dropping translations.
    title_i18n: pruneI18n(f.title_i18n),
    message_i18n: pruneI18n(f.message_i18n),
    display_mode: f.display_mode,
    dismiss_mode: f.dismiss_mode,
    severity: f.severity,
    audience_mode: f.audience_mode,
    start_at: localToIso(f.start_at),
    end_at: localToIso(f.end_at),
    is_active: f.is_active,
  }
}

// Admin-only surface (gated by BannerAdminRoute + backend require_admin).
export default function AdminBannersPage() {
  const { t } = useTranslation(['pages', 'workspace'])
  const list = useAdminBanners()
  const createMut = useCreateBanner()
  const updateMut = useUpdateBanner()
  const deleteMut = useDeleteBanner()

  const [editingId, setEditingId] = useState<string | null>(null)
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [error, setError] = useState<string | null>(null)
  // Which locale the preview renders. Korean and Vietnamese wrap differently from
  // English, so without this an admin publishes translated copy sight-unseen.
  const [previewLocale, setPreviewLocale] = useState<SupportedLocale>('en')
  // The row as loaded, so an edit can tell whether the English base actually
  // changed. Translations are never cleared implicitly server-side; this is the
  // disclosure half of that bargain.
  const [editingOriginal, setEditingOriginal] = useState<AdminBanner | null>(null)

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }))

  const startEdit = (b: AdminBanner) => {
    setEditingId(b.id)
    setEditingOriginal(b)
    setForm(bannerToForm(b))
    setError(null)
    log({
      event: 'admin-edit-start',
      feature: 'dynamic-banner',
      banner_id: b.id,
    })
  }

  const resetForm = () => {
    setEditingId(null)
    setEditingOriginal(null)
    setForm(EMPTY_FORM)
    setError(null)
  }

  // Editing the English base leaves existing translations describing superseded
  // copy. The API preserves them (clearing would destroy data on an old client's
  // full-object PATCH) and logs i18n_staleness_risk; the admin gets this warning.
  const staleTranslationLocales = useMemo(() => {
    if (!editingOriginal) return []
    const locales = new Set<string>()
    const check = (baseKey: 'title' | 'message', mapKey: 'title_i18n' | 'message_i18n') => {
      if (form[baseKey].trim() === editingOriginal[baseKey].trim()) return
      for (const [locale, text] of Object.entries(form[mapKey])) {
        if (text.trim()) locales.add(locale)
      }
    }
    check('title', 'title_i18n')
    check('message', 'message_i18n')
    return [...locales].sort()
  }, [editingOriginal, form])

  // Preview resolves through the SAME ladder the live banner uses, so what the
  // admin sees here is what a viewer in that locale gets — including the English
  // fallback when an override is missing.
  const previewTitle = resolveBannerText(
    form.title || t('adminBanners.previewUntitled'),
    pruneI18n(form.title_i18n),
    previewLocale,
  ).text
  const previewMessage = resolveBannerText(
    form.message || t('adminBanners.previewEmptyMessage'),
    pruneI18n(form.message_i18n),
    previewLocale,
  ).text

  const canSubmit =
    form.title.trim().length > 0 &&
    form.message.trim().length > 0 &&
    !createMut.isPending &&
    !updateMut.isPending

  const handleSubmit = async () => {
    if (!canSubmit) return
    setError(null)
    const payload = formToPayload(form)
    if (payload.start_at && payload.end_at && payload.end_at <= payload.start_at) {
      setError(t('adminBanners.errorEndBeforeStart'))
      return
    }
    try {
      if (editingId) {
        await updateMut.mutateAsync({ id: editingId, input: payload })
        log({
          event: 'admin-update',
          feature: 'dynamic-banner',
          banner_id: editingId,
        })
      } else {
        const created = await createMut.mutateAsync(payload)
        log({
          event: 'admin-create',
          feature: 'dynamic-banner',
          banner_id: created.id,
        })
      }
      resetForm()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('adminBanners.errorSave'))
    }
  }

  const handleDelete = async (b: AdminBanner) => {
    if (!window.confirm(t('adminBanners.confirmDelete', { title: b.title }))) return
    try {
      await deleteMut.mutateAsync(b.id)
      log({
        event: 'admin-delete',
        feature: 'dynamic-banner',
        banner_id: b.id,
      })
      if (editingId === b.id) resetForm()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('adminBanners.errorDelete'))
    }
  }

  const popupAccent = POPUP_SEVERITY_CLASSES[form.severity]
  const PopupAccentIcon = popupAccent.Icon
  const banners = useMemo(() => list.data ?? [], [list.data])

  return (
    <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6" data-testid="admin-banners-page">
      <AdminToolNav />
      <h1 className="text-xl font-semibold text-foreground mb-1">{t('adminBanners.title')}</h1>
      <p className="text-sm text-foreground/50 mb-6">{t('adminBanners.description')}</p>

      <div className="grid gap-8 md:grid-cols-2">
        {/* ---- Form ---- */}
        <section data-testid="admin-banners-form">
          <h2 className="text-sm font-semibold text-foreground mb-3">
            {editingId ? t('adminBanners.editHeading') : t('adminBanners.createHeading')}
          </h2>

          <label className="block mb-3">
            <span className="text-xs font-medium text-foreground/70">
              {t('adminBanners.fieldTitle')}
            </span>
            <input
              type="text"
              value={form.title}
              maxLength={200}
              onChange={(e) => set('title', e.target.value)}
              data-testid="banner-field-title"
              className="mt-1 w-full rounded-md border border-border bg-surface-1 px-3 py-2 text-sm text-foreground transition-colors hover:border-foreground/30 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
            />
          </label>

          <label className="block mb-3">
            <span className="text-xs font-medium text-foreground/70">
              {t('adminBanners.fieldMessage')}
            </span>
            <textarea
              value={form.message}
              maxLength={5000}
              rows={4}
              onChange={(e) => set('message', e.target.value)}
              data-testid="banner-field-message"
              className="mt-1 w-full rounded-md border border-border bg-surface-1 px-3 py-2 text-sm text-foreground transition-colors hover:border-foreground/30 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary font-mono"
            />
          </label>

          {/*
            Optional per-locale overrides. Only non-English locales are offered:
            the fields above ARE the English copy, and anything a viewer's locale
            lacks falls back to them.
          */}
          {/*
            Rendered OUTSIDE the collapsed section on purpose: the English fields
            are above it, so an admin editing English while the section is closed
            would otherwise never see this.
          */}
          {staleTranslationLocales.length > 0 && (
            <p
              className="mb-3 rounded border border-warning/40 bg-warning/10 px-3 py-2 text-xs font-medium text-warning"
              data-testid="banner-translations-stale-hint"
            >
              {t('adminBanners.translationsStaleHint')}
            </p>
          )}

          <details
            className="mb-3 rounded-md border border-border bg-surface-1/50"
            open={staleTranslationLocales.length > 0}
          >
            <summary
              className="cursor-pointer px-3 py-2 text-xs font-medium text-foreground/70 select-none"
              data-testid="banner-translations-toggle"
            >
              {t('adminBanners.translationsHeading')}
            </summary>
            <div className="px-3 pb-3">
              <p className="mb-3 text-xs font-medium leading-relaxed text-foreground/50">
                {t('adminBanners.translationsHint')}
              </p>
              {TRANSLATABLE_LOCALE_OPTIONS.map((opt) => (
                <div key={opt.code} className="mb-3 last:mb-0">
                  <div className="mb-1 text-xs font-semibold text-foreground/60">{opt.label}</div>
                  <input
                    type="text"
                    value={form.title_i18n[opt.code] ?? ''}
                    maxLength={200}
                    placeholder={t('adminBanners.fieldTitle')}
                    onChange={(e) =>
                      set('title_i18n', {
                        ...form.title_i18n,
                        [opt.code]: e.target.value,
                      })
                    }
                    data-testid={`banner-field-title-${opt.code}`}
                    className="mb-1.5 w-full rounded-md border border-border bg-surface-1 px-3 py-2 text-sm text-foreground transition-colors hover:border-foreground/30 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  />
                  <textarea
                    value={form.message_i18n[opt.code] ?? ''}
                    maxLength={5000}
                    rows={3}
                    placeholder={t('adminBanners.fieldMessage')}
                    onChange={(e) =>
                      set('message_i18n', {
                        ...form.message_i18n,
                        [opt.code]: e.target.value,
                      })
                    }
                    data-testid={`banner-field-message-${opt.code}`}
                    className="w-full rounded-md border border-border bg-surface-1 px-3 py-2 text-sm text-foreground transition-colors hover:border-foreground/30 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary font-mono"
                  />
                </div>
              ))}
            </div>
          </details>

          <div className="grid grid-cols-2 gap-3">
            <label className="block mb-3">
              <span className="text-xs font-medium text-foreground/70">
                {t('adminBanners.fieldDisplayMode')}
              </span>
              <select
                value={form.display_mode}
                onChange={(e) => set('display_mode', toDisplayMode(e.target.value))}
                data-testid="banner-field-display-mode"
                className="mt-1 w-full rounded-md border border-border bg-surface-1 px-3 py-2 text-sm text-foreground transition-colors hover:border-foreground/30 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              >
                <option value="banner">{t('adminBanners.displayBanner')}</option>
                <option value="popup">{t('adminBanners.displayPopup')}</option>
              </select>
            </label>

            <label className="block mb-3">
              <span className="text-xs font-medium text-foreground/70">
                {t('adminBanners.fieldDismissMode')}
              </span>
              <select
                value={form.dismiss_mode}
                onChange={(e) => set('dismiss_mode', toDismissMode(e.target.value))}
                data-testid="banner-field-dismiss-mode"
                className="mt-1 w-full rounded-md border border-border bg-surface-1 px-3 py-2 text-sm text-foreground transition-colors hover:border-foreground/30 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              >
                <option value="recurrent">{t('adminBanners.dismissRecurrent')}</option>
                <option value="one_time">{t('adminBanners.dismissOneTime')}</option>
              </select>
            </label>

            <label className="block mb-3">
              <span className="text-xs font-medium text-foreground/70">
                {t('adminBanners.fieldSeverity')}
              </span>
              <select
                value={form.severity}
                onChange={(e) => set('severity', toSeverity(e.target.value))}
                data-testid="banner-field-severity"
                className="mt-1 w-full rounded-md border border-border bg-surface-1 px-3 py-2 text-sm text-foreground transition-colors hover:border-foreground/30 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              >
                <option value="info">{t('adminBanners.severityInfo')}</option>
                <option value="warning">{t('adminBanners.severityWarning')}</option>
                <option value="critical">{t('adminBanners.severityCritical')}</option>
              </select>
            </label>

            <label className="block mb-3">
              <span className="text-xs font-medium text-foreground/70">
                {t('adminBanners.fieldAudience')}
              </span>
              <select
                value={form.audience_mode}
                onChange={(e) => set('audience_mode', toAudience(e.target.value))}
                data-testid="banner-field-audience"
                className="mt-1 w-full rounded-md border border-border bg-surface-1 px-3 py-2 text-sm text-foreground transition-colors hover:border-foreground/30 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              >
                <option value="all">{t('adminBanners.audienceAll')}</option>
                <option value="authenticated">{t('adminBanners.audienceAuthenticated')}</option>
              </select>
            </label>

            <label className="block mb-3">
              <span className="text-xs font-medium text-foreground/70">
                {t('adminBanners.fieldStartAt')}
              </span>
              <input
                type="datetime-local"
                value={form.start_at}
                onChange={(e) => set('start_at', e.target.value)}
                data-testid="banner-field-start-at"
                className="mt-1 w-full rounded-md border border-border bg-surface-1 px-3 py-2 text-sm text-foreground transition-colors hover:border-foreground/30 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              />
            </label>

            <label className="block mb-3">
              <span className="text-xs font-medium text-foreground/70">
                {t('adminBanners.fieldEndAt')}
              </span>
              <input
                type="datetime-local"
                value={form.end_at}
                onChange={(e) => set('end_at', e.target.value)}
                data-testid="banner-field-end-at"
                className="mt-1 w-full rounded-md border border-border bg-surface-1 px-3 py-2 text-sm text-foreground transition-colors hover:border-foreground/30 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              />
            </label>
          </div>

          <label className="flex items-center gap-2 mb-4 text-sm text-foreground/80">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => set('is_active', e.target.checked)}
              data-testid="banner-field-is-active"
              className="h-4 w-4 rounded border-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            />
            {t('adminBanners.fieldIsActive')}
          </label>

          {error && (
            <p className="mb-3 text-sm text-red-400" data-testid="admin-banners-error">
              {error}
            </p>
          )}

          <div className="flex gap-2">
            <button
              type="button"
              disabled={!canSubmit}
              onClick={handleSubmit}
              data-testid="banner-submit"
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-40"
            >
              {editingId ? t('adminBanners.saveChanges') : t('adminBanners.publish')}
            </button>
            {editingId && (
              <button
                type="button"
                onClick={resetForm}
                data-testid="banner-cancel-edit"
                className="rounded-md bg-surface-2 px-4 py-2 text-sm font-medium text-foreground hover:bg-surface-3"
              >
                {t('adminBanners.cancel')}
              </button>
            )}
          </div>
        </section>

        {/* ---- Live preview ---- */}
        <section data-testid="admin-banners-preview">
          <div className="mb-3 flex items-center justify-between gap-3">
            <h2 className="text-sm font-semibold text-foreground">
              {t('adminBanners.previewHeading')}
            </h2>
            <label className="flex items-center gap-2 text-xs font-medium text-foreground/60">
              {t('adminBanners.previewLocale')}
              <select
                value={previewLocale}
                onChange={(e) => setPreviewLocale(normalizeLocale(e.target.value) ?? 'en')}
                data-testid="banner-preview-locale"
                className="rounded-md border border-border bg-surface-1 px-2 py-1 text-xs font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {SUPPORTED_LOCALE_OPTIONS.map((opt) => (
                  <option key={opt.code} value={opt.code}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {form.display_mode === 'banner' ? (
            <StickyBannerStrip
              preview
              severity={form.severity}
              title={previewTitle}
              message={previewMessage}
              renderMarkdown
              dismissible
              onDismiss={() => {}}
              dismissLabel={t('adminBanners.previewDismiss')}
              testId="banner-preview-strip"
            />
          ) : (
            <div
              className="rounded-lg border border-border bg-surface-1 p-6 text-foreground shadow-lg"
              data-testid="banner-preview-popup"
              data-severity={form.severity}
            >
              <h3 className={`flex items-center gap-2 text-lg font-semibold ${popupAccent.accent}`}>
                <PopupAccentIcon aria-hidden="true" className="h-5 w-5 flex-shrink-0" />
                <span>{previewTitle}</span>
              </h3>
              <div className="mt-3 text-sm leading-relaxed text-foreground/80">
                <BannerMarkdown>{previewMessage}</BannerMarkdown>
              </div>
              {form.dismiss_mode === 'one_time' && (
                <label className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
                  <input type="checkbox" disabled className="h-4 w-4" />
                  {t('banners.dontShowAgain', { ns: 'workspace' })}
                </label>
              )}
            </div>
          )}
        </section>
      </div>

      {/* ---- Existing banners ---- */}
      <section className="mt-10" data-testid="admin-banners-list">
        <h2 className="text-sm font-semibold text-foreground mb-3">
          {t('adminBanners.existingHeading')}
        </h2>
        {list.isLoading ? (
          <p className="text-sm text-foreground/50">{t('adminBanners.loading')}</p>
        ) : banners.length === 0 ? (
          <p className="text-sm text-foreground/50">{t('adminBanners.empty')}</p>
        ) : (
          <ul className="divide-y divide-border rounded-md border border-border">
            {banners.map((b) => (
              <li
                key={b.id}
                data-testid={`banner-row-${b.id}`}
                className="flex items-center justify-between gap-4 px-4 py-3"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-sm font-medium text-foreground">{b.title}</span>
                    <span className="rounded bg-surface-2 px-1.5 py-0.5 text-xs font-medium uppercase text-foreground/60">
                      {b.display_mode}
                    </span>
                    <span className="rounded bg-surface-2 px-1.5 py-0.5 text-xs font-medium uppercase text-foreground/60">
                      {b.severity}
                    </span>
                    {!b.is_active && (
                      <span className="rounded bg-surface-2 px-1.5 py-0.5 text-xs font-medium uppercase text-foreground/40">
                        {t('adminBanners.inactiveTag')}
                      </span>
                    )}
                  </div>
                  <p className="truncate text-xs font-medium text-foreground/50">{b.message}</p>
                </div>
                <div className="flex flex-shrink-0 gap-2">
                  <button
                    type="button"
                    onClick={() => startEdit(b)}
                    data-testid={`banner-edit-${b.id}`}
                    className="rounded-md bg-surface-2 px-3 py-1.5 text-xs font-medium text-foreground hover:bg-surface-3"
                  >
                    {t('adminBanners.edit')}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(b)}
                    data-testid={`banner-delete-${b.id}`}
                    className="rounded-md bg-red-500/20 px-3 py-1.5 text-xs font-medium text-red-300 hover:bg-red-500/30"
                  >
                    {t('adminBanners.delete')}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  )
}
